import React, { useState, useEffect } from 'react'
import { Box } from '@mui/system'
import { makeStyles } from '@material-ui/core'
import { sliderClasses } from '@mui/base/Slider'
import { Slider } from '@mui/base'

const DEFAULT_COLORS = ['#549FF5', '#1DBE1D', '#FDD71A', '#ED1C24'] // blue, green, yellow, red

const HMapColorSlider = ({
  width,
  name,
  defaultValues,
  colors,
  onChange,
  disabled
}) => {
  const [value, setValue] = useState([0, 35, 100])
  const palette = Array.isArray(colors) && colors.length === 4 ? colors : DEFAULT_COLORS
  const classes = useStyles({ width, value, palette })

  const handleChange = (event, newValue) => {
    const minDistance = 5
    const v = Array.isArray(newValue) ? newValue : value
    const res = v.map((val, idx) => {
      if (idx === 1) {
        if (val < 10) return 10
        if (val > v[2]) return v[2] - minDistance
        return val
      }
      return val
    })
    setValue(res)
    onChange?.({ target: { value: [...res], name } })
  }

  const SliderValueLabel = ({ index, children }) => (
    <span data-index={`${index}`} className="label">{`${children}%`}</span>
  )

  useEffect(() => {
    setValue((!!defaultValues && [...defaultValues]) || [0, 35, 100])
  }, [defaultValues])

  return (
    <Box className={classes.boxi}>
      <Slider
        className={classes.root}
        name={name}
        value={value}
        onChange={handleChange}
        getAriaLabel={() => 'Color percentage thresholds'}
        min={0}
        max={100}
        slots={{ valueLabel: SliderValueLabel }}
        disabled={!!disabled}
      />
    </Box>
  )
}

export default React.memo(HMapColorSlider)

const useStyles = makeStyles((theme) => ({
  boxi: {
    width: (props) => (props.width ? props.width : '100%'),
    height: '50px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-end',
    padding: '0 0 10px 0'
  },
  root: {
    width: '89%',
    display: 'inline-block',
    position: 'relative',
    cursor: 'pointer',
    'touch-action': 'none',
    '-webkit-tap-highlight-color': 'transparent',

    [`&.${sliderClasses.disabled}`]: {},

    [`& .${sliderClasses.rail}`]: {
      display: 'block',
      position: 'absolute',
      width: '100%',
      height: '4px',
      borderRadius: '2px',
      background: (props) => {
        const [c0, c1, c2, c3] = props.palette
        const [t0, t1, t2] = props.value
        return `linear-gradient(to right,
          ${c0} 0% ${t0}%,
          ${c1} ${t0}% ${t1}%,
          ${c2} ${t1}% ${t2}%,
          ${c3} ${t2}% 100%)`
      }
    },

    [`& .${sliderClasses.track}`]: {
      display: 'block',
      position: 'absolute',
      height: '4px',
      borderRadius: '2px'
    },

    [`& .${sliderClasses.thumb}`]: {
      position: 'absolute',
      zIndex: 20,
      marginLeft: '-6px',
      marginTop: '-6px',
      boxSizing: 'border-box',
      outline: 0,
      border: `1px solid ${theme.palette.primary.main}`,
      background: theme.palette.primary.main,
      padding: '0px',
      borderRadius: '50%',
      width: '10px',
      height: '10px',
      transform: 'translate(-0px, 3px)',
      '&[data-index="0"]': {
        backgroundColor: (p) => p.palette[0],
        borderColor: (p) => p.palette[0]
      },
      '&[data-index="1"]': {
        backgroundColor: (p) => p.palette[1],
        borderColor: (p) => p.palette[1]
      },
      '&[data-index="2"]': {
        backgroundColor: (p) => p.palette[3],
        borderColor: (p) => p.palette[3]
      },
      '& .label': {
        position: 'absolute',
        zIndex: 30,
        background: 'white',
        border: `0.7px solid ${theme.palette.tertiary.accent}`,
        borderRadius: '5px',
        padding: '3px',
        ...theme.typography.p,
        color: theme.palette.tertiary.accent,
        '&[data-index="0"]': { transform: 'translate(-10px, -35px)' },
        '&[data-index="1"]': { transform: 'translate(-10px, -35px)' },
        '&[data-index="2"]': { transform: 'translate(-15px, -35px)' }
      }
    },

    [`& .${sliderClasses.mark}`]: {
      position: 'absolute',
      width: '4px',
      height: '4px',
      borderRadius: '2px',
      backgroundColor: 'currentColor',
      top: '50%',
      opacity: '0.7',
      transform: 'translateX(-50%)'
    },

    [`& .${sliderClasses.markActive}`]: {
      backgroundColor: '#fff'
    }
  }
}))